import {
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type MouseEvent,
  type SubmitEvent,
} from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Link,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { SchoolOption } from "../../services/appSchoolService";
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { ConfirmationDialog } from "../../components/ConfirmationDialog";
import { useAssessmentBenchmark } from "../../hooks/useAssessmentBenchmark";
import { useDistrictsFilter } from "../../hooks/useDistrictsFilter";
import { useSchoolsFilter } from "../../hooks/useSchoolsFilter";
import { usePredefinedGradesFilter } from "../../hooks/usePredefinedGradesFilter";
import { GradeScope, primaryGrades, secondaryGrades } from "./index";
import { hasSameValues } from "../../lib/helpers";
import { BenchmarkImageField } from "./BenchmarkImageField";

type CreateBenchmarkLocationState = {
  benchmarkImageError?: string;
};

const textFieldSlotProps = {
  inputLabel: { shrink: true },
};

type GradeOption = {
  id: string;
  grade: string;
};

function getGradeIdsForScope(
  gradeScope: GradeScope,
  grades: GradeOption[],
): string[] {
  switch (gradeScope) {
    case "primary":
      return grades
        .filter((grade) => primaryGrades.includes(grade.grade))
        .map((grade) => grade.id);
    case "secondary":
      return grades
        .filter((grade) => secondaryGrades.includes(grade.grade))
        .map((grade) => grade.id);
    case "all":
      return [];
  }
}

function getGradeScopeFromGradeIds(
  gradeIds: string[],
  grades: GradeOption[],
): GradeScope | null {
  if (gradeIds.length === 0 || gradeIds.length === grades.length) {
    return "all";
  }

  const selectedGradeLabels = grades
    .filter((grade) => gradeIds.includes(grade.id))
    .map((grade) => grade.grade);
  const availablePrimaryLabels = grades
    .filter((grade) => primaryGrades.includes(grade.grade))
    .map((grade) => grade.grade);
  const availableSecondaryLabels = grades
    .filter((grade) => secondaryGrades.includes(grade.grade))
    .map((grade) => grade.grade);

  if (hasSameValues(selectedGradeLabels, availablePrimaryLabels)) {
    return "primary";
  }
  if (hasSameValues(selectedGradeLabels, availableSecondaryLabels)) {
    return "secondary";
  }

  return null;
}

const openDatePicker = (event: MouseEvent<HTMLInputElement>) => {
  const input = event.currentTarget;
  if (typeof input.showPicker !== "function") {
    return;
  }
  try {
    input.showPicker();
  } catch {
    return;
  }
};

const dateFieldSlotProps = {
  ...textFieldSlotProps,
  htmlInput: { onClick: openDatePicker },
};

const dateFieldSx = {
  "& input[type='date']": {
    cursor: "pointer",
  },
};

const SCHOOL_MENU_MAX_HEIGHT = 280;
const DISTRICT_MENU_MAX_HEIGHT = 280;
const ALL_DISTRICTS_VALUE = "__all_districts__";
const ALL_SCHOOLS_VALUE = "__all_schools__";

const BENCHMARK_TYPE_LABELS: Record<string, string> = {
  boy: "BOY",
  moy: "MOY",
  eoy: "EOY",
};

const SKILL_LABELS: Record<string, string> = {
  reading: "Reading",
  listening: "Listening",
  speaking: "Speaking",
  writing: "Writing",
};

function singleSelectSlotProps(
  placeholder: string,
  resolveLabel?: (value: string) => string,
) {
  return {
    ...textFieldSlotProps,
    select: {
      displayEmpty: true,
      renderValue: (selected: unknown) => {
        const value = String(selected ?? "");
        if (!value) {
          return placeholder;
        }
        return resolveLabel?.(value) ?? value;
      },
    },
  };
}

function formatSelectedSchoolLabels(
  selectedSchoolIds: string[],
  schools: SchoolOption[],
): string {
  if (selectedSchoolIds.length === 0) {
    return "";
  }
  return schools
    .filter((school) => selectedSchoolIds.includes(school.id))
    .map((school) => school.name)
    .join(", ");
}

function formatSelectedDistrictLabels(
  selectedDistrictIds: string[],
  districts: { id: string; name: string }[],
): string {
  if (selectedDistrictIds.length === 0) {
    return "";
  }

  return districts
    .filter((district) => selectedDistrictIds.includes(district.id))
    .map((district) => district.name)
    .join(", ");
}

function toDateInputValue(value?: string | null): string {
  if (!value) {
    return "";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }
  return parsed.toISOString().slice(0, 10);
}

export function CreateBenchmarkPage() {
  const { benchmarkId } = useParams<{ benchmarkId?: string }>();
  return <BenchmarkFormPage key={benchmarkId?.trim() || "create"} />;
}

function BenchmarkFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { benchmarkId } = useParams<{ benchmarkId?: string }>();
  const isEditing = Boolean(benchmarkId?.trim());
  const {
    isSavingBenchmark,
    isDeletingBenchmark,
    isUploadingImage,
    createBenchmark,
    uploadBenchmarkImage,
    getBenchmark,
    deleteBenchmark,
  } = useAssessmentBenchmark();
  const {
    districts,
    isLoading: isDistrictsLoading,
    error: districtsError,
  } = useDistrictsFilter();
  const {
    grades,
    isLoading: isGradesLoading,
    error: gradesError,
  } = usePredefinedGradesFilter();

  const [formError, setFormError] = useState<string | null>(null);
  const [isLoadingBenchmark, setIsLoadingBenchmark] = useState(isEditing);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [benchmarkType, setBenchmarkType] = useState<
    "boy" | "moy" | "eoy" | ""
  >("");
  const [skill, setSkill] = useState<
    "reading" | "listening" | "speaking" | "writing" | ""
  >("");
  const [userGradeScope, setUserGradeScope] = useState<GradeScope | null>(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedDistrictIds, setSelectedDistrictIds] = useState<string[]>([]);
  const [selectedSchoolIds, setSelectedSchoolIds] = useState<string[]>([]);
  const [selectedGradeIds, setSelectedGradeIds] = useState<string[]>([]);
  const [allSchoolsSelected, setAllSchoolsSelected] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [savedImageUrl, setSavedImageUrl] = useState<string | null>(null);
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [isImageRemoved, setIsImageRemoved] = useState(false);
  const imageWarning =
    (location.state as CreateBenchmarkLocationState | null)
      ?.benchmarkImageError ?? null;

  const handleImageSelect = (file: File) => {
    setPendingImageFile(file);
    if (imageWarning) {
      navigate(location.pathname, { replace: true, state: null });
    }
  };

  const loadedGradeScope = useMemo((): GradeScope | null => {
    if (selectedGradeIds.length === 0 || grades.length === 0) {
      return "all";
    }
    return getGradeScopeFromGradeIds(selectedGradeIds, grades);
  }, [selectedGradeIds, grades]);
  const gradeScope = userGradeScope ?? loadedGradeScope ?? "all";
  const gradeScopeRadioValue = userGradeScope ?? loadedGradeScope ?? "";

  const effectiveSelectedGradeIds = useMemo(
    () =>
      selectedGradeIds.length > 0
        ? selectedGradeIds
        : getGradeIdsForScope(gradeScope, grades),
    [gradeScope, grades, selectedGradeIds],
  );
  const {
    schools,
    isLoading: isSchoolsLoading,
    error: schoolsError,
  } = useSchoolsFilter({
    districtIds: selectedDistrictIds,
    gradeIds: effectiveSelectedGradeIds,
  });
  function handleGradeChange(event: ChangeEvent<HTMLInputElement>) {
    if (isGradesLoading || gradesError) {
      return;
    }
    const nextGradeScope = event.target.value as GradeScope;
    setUserGradeScope(nextGradeScope);
    setSelectedGradeIds(getGradeIdsForScope(nextGradeScope, grades));
    setSelectedSchoolIds([]);
    setAllSchoolsSelected(false);
  }

  const isAllDistrictsSelected =
    districts.length > 0 && selectedDistrictIds.length === districts.length;
  const districtSelectValue = isAllDistrictsSelected
    ? [ALL_DISTRICTS_VALUE, ...selectedDistrictIds]
    : selectedDistrictIds;
  const schoolSelectValue = allSchoolsSelected
    ? [ALL_SCHOOLS_VALUE, ...schools.map((school) => school.id)]
    : selectedSchoolIds;

  useEffect(() => {
    if (!benchmarkId?.trim()) {
      return;
    }

    let cancelled = false;

    getBenchmark(benchmarkId)
      .then((benchmark) => {
        if (cancelled) {
          return;
        }
        setTitle(benchmark.title);
        setDescription(benchmark.description ?? "");
        setBenchmarkType(benchmark.benchmark_type);
        setSkill(benchmark.skill);
        setStartDate(toDateInputValue(benchmark.start_at));
        setEndDate(toDateInputValue(benchmark.close_at));
        setUserGradeScope(null);
        setSelectedDistrictIds(benchmark.district_ids ?? []);
        setSelectedSchoolIds(benchmark.school_ids ?? []);
        setSelectedGradeIds(benchmark.grade_ids ?? []);
        setAllSchoolsSelected((benchmark.school_ids ?? []).length === 0);
        setSavedImageUrl(benchmark.image_url ?? null);
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }
        setFormError(
          error instanceof Error
            ? error.message
            : "Unable to load assessment benchmark.",
        );
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingBenchmark(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [benchmarkId, getBenchmark]);

  const handleDistrictChange = (districtValues: string[]) => {
    const includesAllOption = districtValues.includes(ALL_DISTRICTS_VALUE);
    const nextDistrictIds = districtValues.filter(
      (value) => value !== ALL_DISTRICTS_VALUE,
    );
    const allDistrictIds = districts.map((district) => district.id);

    if (districts.length === 0) {
      setSelectedDistrictIds([]);
      setSelectedSchoolIds([]);
      setAllSchoolsSelected(false);
      return;
    }

    const wasAllSelected = isAllDistrictsSelected;
    let resolvedIds: string[];
    if (includesAllOption && !wasAllSelected) {
      resolvedIds = allDistrictIds;
    } else if (!includesAllOption && wasAllSelected) {
      resolvedIds = [];
    } else {
      resolvedIds = nextDistrictIds;
    }

    setSelectedDistrictIds(resolvedIds);
    setSelectedSchoolIds([]);
    setAllSchoolsSelected(false);
  };

  const handleSchoolChange = (schoolValues: string[]) => {
    const includesAllOption = schoolValues.includes(ALL_SCHOOLS_VALUE);
    const nextSchoolIds = schoolValues.filter(
      (value) => value !== ALL_SCHOOLS_VALUE,
    );
    const allSchoolIds = schools.map((school) => school.id);

    if (schools.length === 0) {
      setSelectedSchoolIds([]);
      setAllSchoolsSelected(false);
      return;
    }

    if (
      (includesAllOption && !allSchoolsSelected) ||
      nextSchoolIds.length === schools.length
    ) {
      setSelectedSchoolIds(allSchoolIds);
      setAllSchoolsSelected(true);
      return;
    }

    setSelectedSchoolIds(nextSchoolIds);
    setAllSchoolsSelected(false);
  };

  const handleSubmit = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedTitle = title.trim();
    const normalizedDescription = description.trim();
    const schoolIdsForPayload = allSchoolsSelected ? [] : selectedSchoolIds;
    const gradeIdsForPayload = effectiveSelectedGradeIds;

    if (
      !normalizedTitle ||
      !benchmarkType ||
      !skill ||
      !startDate ||
      !endDate ||
      selectedDistrictIds.length === 0
    ) {
      setFormError(
        "Title, benchmark type, skill, district, start date, and end date are required.",
      );
      return;
    }

    const datePattern = /^\d{4}-\d{2}-\d{2}$/;
    if (
      !datePattern.test(startDate) ||
      !datePattern.test(endDate) ||
      endDate < startDate
    ) {
      setFormError(
        "Start date and end date must be valid, and end date must be on or after start date.",
      );
      return;
    }

    const benchmarkPayload = {
      title: normalizedTitle,
      description: normalizedDescription,
      benchmark_type: benchmarkType,
      skill,
      start_date: startDate,
      end_date: endDate,
      district_ids: selectedDistrictIds,
      school_ids: schoolIdsForPayload,
      grade_ids: gradeIdsForPayload,
    };
    const successState = {
      benchmarkMessage: `Benchmark "${normalizedTitle}" ${isEditing ? "updated" : "created"}.`,
    };

    try {
      setFormError(null);
      const editingId = benchmarkId?.trim();
      if (editingId) {
        const image = pendingImageFile
          ? await uploadBenchmarkImage(editingId, pendingImageFile)
          : undefined;
        await createBenchmark({
          ...benchmarkPayload,
          id: editingId,
          ...(image ? { image } : {}),
          ...(!image && isImageRemoved && savedImageUrl
            ? { remove_image: true }
            : {}),
        });
        navigate("/assessments-admin/benchmarks", { state: successState });
        return;
      }

      const created = await createBenchmark(benchmarkPayload);
      if (pendingImageFile) {
        try {
          const image = await uploadBenchmarkImage(
            created.id,
            pendingImageFile,
          );
          await createBenchmark({ ...benchmarkPayload, id: created.id, image });
        } catch {
          navigate(`/assessments-admin/benchmarks/${created.id}/edit`, {
            replace: true,
            state: {
              benchmarkImageError: `Benchmark "${normalizedTitle}" was created, but its image didn't upload. Choose the image again and save.`,
            },
          });
          return;
        }
      }
      navigate("/assessments-admin/benchmarks", { state: successState });
    } catch (saveBenchmarkError: unknown) {
      setFormError(
        saveBenchmarkError instanceof Error
          ? saveBenchmarkError.message
          : "Unable to save assessment benchmark.",
      );
    }
  };

  const handleDelete = async () => {
    if (!benchmarkId?.trim()) {
      return;
    }
    const normalizedTitle = title.trim() || "this benchmark";

    try {
      setFormError(null);
      await deleteBenchmark(benchmarkId);
      setIsDeleteDialogOpen(false);
      navigate("/assessments-admin/benchmarks", {
        state: {
          benchmarkMessage: `Benchmark "${normalizedTitle}" deleted.`,
        },
      });
    } catch (deleteError: unknown) {
      setFormError(
        deleteError instanceof Error
          ? deleteError.message
          : "Unable to delete assessment benchmark.",
      );
    }
  };

  const isSubmitting =
    isSavingBenchmark || isDeletingBenchmark || isUploadingImage;
  const pageTitle = isEditing
    ? "Edit Assessment Benchmark"
    : "Create Assessment Benchmark";
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={2}>
        <Box>
          <Link
            component={RouterLink}
            to="/assessments-admin/benchmarks"
            underline="hover"
            sx={{ display: "inline-block", mb: 1.5, fontSize: 14 }}
          >
            Back to benchmarks
          </Link>
          <Typography variant="h4" component="h1" gutterBottom>
            {pageTitle}
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          aria-label={pageTitle}
        >
          {imageWarning ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {imageWarning}
            </Alert>
          ) : null}
          {formError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          ) : null}
          {districtsError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {districtsError}
            </Alert>
          ) : null}
          {schoolsError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {schoolsError}
            </Alert>
          ) : null}
          {gradesError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {gradesError}
            </Alert>
          ) : null}
          {isLoadingBenchmark ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Loading benchmark...
            </Alert>
          ) : null}

          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    md: "minmax(0, 1.45fr) minmax(0, 1fr)",
                  },
                  gridTemplateAreas: {
                    xs: '"title" "image" "description" "district"',
                    md: '"title image" "description image" "district image"',
                  },
                  gap: 2,
                }}
              >
                <TextField
                  label="Benchmark Title"
                  name="title"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  required
                  placeholder="26-27 BOY Reading"
                  fullWidth
                  sx={{ gridArea: "title" }}
                  slotProps={textFieldSlotProps}
                />
                <TextField
                  label="Description"
                  name="description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="Describe what this benchmark covers"
                  fullWidth
                  multiline
                  minRows={3}
                  disabled={isLoadingBenchmark || isSubmitting}
                  sx={{ gridArea: "description" }}
                  slotProps={textFieldSlotProps}
                />
                <TextField
                  select
                  label="District/s"
                  name="district_ids"
                  value={districtSelectValue}
                  required
                  fullWidth
                  disabled={
                    isDistrictsLoading || isLoadingBenchmark || isSubmitting
                  }
                  sx={{ gridArea: "district" }}
                  slotProps={{
                    ...textFieldSlotProps,
                    select: {
                      multiple: true,
                      displayEmpty: true,
                      onChange: (event) => {
                        const { value } = event.target as { value: string[] };
                        handleDistrictChange(value);
                      },
                      renderValue: (selected) => {
                        const idsOnly = ((selected as string[]) ?? []).filter(
                          (value) => value !== ALL_DISTRICTS_VALUE,
                        );
                        if (isAllDistrictsSelected) {
                          return "All Districts";
                        }
                        return (
                          formatSelectedDistrictLabels(idsOnly, districts) ||
                          "Select District/s"
                        );
                      },
                      MenuProps: {
                        PaperProps: {
                          sx: { maxHeight: DISTRICT_MENU_MAX_HEIGHT },
                        },
                      },
                    },
                  }}
                  helperText={
                    isDistrictsLoading
                      ? "Loading districts..."
                      : selectedDistrictIds.length === 0
                        ? "Select district first"
                        : isAllDistrictsSelected
                          ? "All districts selected"
                          : `${selectedDistrictIds.length} district(s) selected`
                  }
                >
                  {isDistrictsLoading ? (
                    <MenuItem disabled value="">
                      Loading districts...
                    </MenuItem>
                  ) : null}
                  <MenuItem value={ALL_DISTRICTS_VALUE}>
                    <Checkbox
                      checked={isAllDistrictsSelected}
                      sx={{ py: 0, pr: 1 }}
                    />
                    <ListItemText primary="All Districts" />
                  </MenuItem>
                  <Divider />
                  {districts.map((district) => (
                    <MenuItem key={district.id} value={district.id}>
                      <Checkbox
                        checked={selectedDistrictIds.includes(district.id)}
                        sx={{ py: 0, pr: 1 }}
                      />
                      <ListItemText primary={district.name} />
                    </MenuItem>
                  ))}
                </TextField>
                <Box sx={{ gridArea: "image", alignSelf: "start" }}>
                  <BenchmarkImageField
                    savedImageUrl={isImageRemoved ? null : savedImageUrl}
                    file={pendingImageFile}
                    disabled={isLoadingBenchmark || isSubmitting}
                    isUploading={isUploadingImage}
                    onSelect={handleImageSelect}
                    onRemove={() => {
                      setPendingImageFile(null);
                      setIsImageRemoved(true);
                    }}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                <TextField
                  select
                  label="Schools"
                  name="school_ids"
                  value={schoolSelectValue}
                  fullWidth
                  disabled={
                    selectedDistrictIds.length === 0 ||
                    isSchoolsLoading ||
                    isLoadingBenchmark ||
                    isSubmitting
                  }
                  sx={{ gridColumn: { md: "1 / -1" } }}
                  slotProps={{
                    ...textFieldSlotProps,
                    select: {
                      multiple: true,
                      displayEmpty: true,
                      onChange: (event) => {
                        const { value } = event.target as { value: string[] };
                        handleSchoolChange(value);
                      },
                      renderValue: (selected) =>
                        (selected as string[])?.includes(ALL_SCHOOLS_VALUE)
                          ? "All Schools"
                          : formatSelectedSchoolLabels(
                              (selected as string[]).filter(
                                (value) => value !== ALL_SCHOOLS_VALUE,
                              ),
                              schools,
                            ) || "Select schools",
                      MenuProps: {
                        PaperProps: {
                          sx: { maxHeight: SCHOOL_MENU_MAX_HEIGHT },
                        },
                      },
                    },
                  }}
                  helperText={
                    selectedDistrictIds.length === 0
                      ? "Select a district first"
                      : isSchoolsLoading
                        ? "Loading schools..."
                        : allSchoolsSelected
                          ? "All schools selected"
                          : selectedSchoolIds.length > 0
                            ? `${selectedSchoolIds.length} school(s) selected`
                            : undefined
                  }
                >
                  {isSchoolsLoading ? (
                    <MenuItem disabled value="">
                      Loading schools...
                    </MenuItem>
                  ) : null}
                  {!isSchoolsLoading &&
                  schools.length === 0 &&
                  selectedDistrictIds.length > 0 ? (
                    <MenuItem disabled value="">
                      No schools found
                    </MenuItem>
                  ) : null}
                  {schools.length > 0 ? (
                    <MenuItem value={ALL_SCHOOLS_VALUE}>
                      <Checkbox
                        checked={allSchoolsSelected}
                        sx={{ py: 0, pr: 1 }}
                      />
                      <ListItemText primary="All Schools" />
                    </MenuItem>
                  ) : null}
                  {schools.length > 0 ? <Divider /> : null}
                  {schools.map((school) => (
                    <MenuItem key={school.id} value={school.id}>
                      <Checkbox
                        checked={
                          allSchoolsSelected ||
                          selectedSchoolIds.includes(school.id)
                        }
                        sx={{ py: 0, pr: 1 }}
                      />
                      <ListItemText primary={school.name} />
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Benchmark Type"
                  name="benchmark_type"
                  value={benchmarkType}
                  onChange={(event) =>
                    setBenchmarkType(
                      event.target.value as "boy" | "moy" | "eoy" | "",
                    )
                  }
                  required
                  fullWidth
                  disabled={isLoadingBenchmark || isSubmitting}
                  slotProps={singleSelectSlotProps(
                    "Select benchmark type",
                    (value) => BENCHMARK_TYPE_LABELS[value] ?? value,
                  )}
                >
                  <MenuItem value="boy">BOY</MenuItem>
                  <MenuItem value="moy">MOY</MenuItem>
                  <MenuItem value="eoy">EOY</MenuItem>
                </TextField>
                <TextField
                  select
                  label="Skill"
                  name="skill"
                  value={skill}
                  onChange={(event) =>
                    setSkill(
                      event.target.value as
                        | "reading"
                        | "listening"
                        | "speaking"
                        | "writing"
                        | "",
                    )
                  }
                  required
                  fullWidth
                  disabled={isLoadingBenchmark || isSubmitting}
                  slotProps={singleSelectSlotProps(
                    "Select skill",
                    (value) => SKILL_LABELS[value] ?? value,
                  )}
                >
                  <MenuItem value="reading">Reading</MenuItem>
                  <MenuItem value="listening">Listening</MenuItem>
                  <MenuItem value="speaking">Speaking</MenuItem>
                  <MenuItem value="writing">Writing</MenuItem>
                </TextField>
                <TextField
                  label="Start Date"
                  name="start_date"
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                  required
                  fullWidth
                  disabled={isLoadingBenchmark || isSubmitting}
                  slotProps={dateFieldSlotProps}
                  sx={dateFieldSx}
                />
                <TextField
                  label="End Date"
                  name="end_date"
                  type="date"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                  required
                  fullWidth
                  disabled={isLoadingBenchmark || isSubmitting}
                  slotProps={dateFieldSlotProps}
                  sx={dateFieldSx}
                />
                <Stack>
                  <FormLabel id={`grades-label`}>Grades: </FormLabel>
                  <RadioGroup
                    value={gradeScopeRadioValue}
                    onChange={handleGradeChange}
                    row
                  >
                    <FormControlLabel
                      value="primary"
                      control={<Radio />}
                      label="Primary (PK to 5)"
                      disabled={
                        isGradesLoading || isLoadingBenchmark || isSubmitting
                      }
                    />
                    <FormControlLabel
                      value="secondary"
                      control={<Radio />}
                      label="Secondary (6 to 12)"
                      disabled={
                        isGradesLoading || isLoadingBenchmark || isSubmitting
                      }
                    />
                    <FormControlLabel
                      value="all"
                      control={<Radio />}
                      label="All (K to 12)"
                      disabled={
                        isGradesLoading || isLoadingBenchmark || isSubmitting
                      }
                    />
                  </RadioGroup>
                  <FormHelperText>
                    * Grades PK–2 are excluded in districts using Benchmarks
                    mode.
                  </FormHelperText>
                </Stack>
              </Box>
              <Stack
                direction={{ xs: "column-reverse", sm: "row" }}
                spacing={1.5}
                justifyContent="space-between"
              >
                <Box>
                  {isEditing ? (
                    <Button
                      type="button"
                      color="error"
                      onClick={() => setIsDeleteDialogOpen(true)}
                      disabled={isLoadingBenchmark || isSubmitting}
                    >
                      DELETE
                    </Button>
                  ) : null}
                </Box>
                <Stack
                  direction={{ xs: "column-reverse", sm: "row" }}
                  spacing={1.5}
                >
                  <Button
                    type="button"
                    onClick={() => navigate("/assessments-admin/benchmarks")}
                    disabled={isLoadingBenchmark || isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isLoadingBenchmark || isSubmitting}
                  >
                    {isSavingBenchmark || isUploadingImage
                      ? "Saving..."
                      : isEditing
                        ? "Save Changes"
                        : "Save Benchmark"}
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </Paper>
        </Box>
      </Stack>
      <ConfirmationDialog
        open={isDeleteDialogOpen}
        title="Delete benchmark?"
        description={`Delete "${title.trim() || "this benchmark"}"? This will soft-delete the benchmark and remove it from active admin flows.`}
        confirmLabel="Delete Benchmark"
        confirmColor="error"
        isConfirming={isDeletingBenchmark}
        onClose={() => {
          if (!isDeletingBenchmark) {
            setIsDeleteDialogOpen(false);
          }
        }}
        onConfirm={handleDelete}
      />
    </Container>
  );
}
